import {useCallback, useMemo, useState} from 'react'
import {useCustomerType, useCustomerId, useCustomer} from '@salesforce/commerce-sdk-react'
import {getTpaParamsFromUrl} from '../utils'
import {
    setTpaBrandingCookiesFromUrl,
    getTpaBrandingValuesFromCookies,
    setTpaBrandingCookiesFromCustomerData,
    formatTpaBrandingCookie,
    determineShouldUpdateTpaBrandingCookie,
    formatTpaBrandingDataForCustomerData
} from './utils'
import useMutateCustomerTpaData from '@/hooks/commerce-sdk/useMutateCustomerTpaData'
import {CookieValuesType, TpaBrandingCookieValues, TpaBrandingFromCustomerProfile} from '../types'
import {useSyncCookiesFromUrl} from '../useSyncTpaCookiesFromUrl'
import {useSyncCookiesFromCustomerData} from '../useSyncCookiesFromCustomerData'
import {useSyncCookieToCustomerProfile} from '../useSyncCookieToCustomerProfile'

const useTpaBrandingCookies = (
    setContextCookiesValue: React.Dispatch<React.SetStateAction<CookieValuesType>>
) => {
    const [tpaCookieValues, setTpaCookieValues] = useState<
        TpaBrandingCookieValues | null | undefined
    >(() => getTpaBrandingValuesFromCookies() ?? undefined)
    const {mutateCustomerTpaBrandingData} = useMutateCustomerTpaData()
    const customerId = useCustomerId()
    const {isRegistered} = useCustomerType()
    const shouldQueryCustomerData = !!customerId && isRegistered
    const {data: customerData, isLoading: isCustomerDataLoading} = useCustomer(
        {parameters: {customerId}},
        {enabled: shouldQueryCustomerData}
    )

    const {c_TPA: tpaObjectFromCustomerData} = customerData || {}
    const tpaValueFromCustomerData = useMemo(() => {
        return customerData ? JSON.parse(tpaObjectFromCustomerData || '{}') : null
    }, [tpaObjectFromCustomerData, customerData])
    const tpaParamsInUrl = useMemo(() => getTpaParamsFromUrl(), [])
    const isTpaParamsInUrl = !!tpaParamsInUrl

    const setContextState = useCallback(
        (values: TpaBrandingCookieValues) => {
            setContextCookiesValue((prev) => ({...prev, tpaBranding: values}))
        },
        [setContextCookiesValue]
    )

    // Sets TPA tracking cookies from URL
    // This is used to set the cookies when the user clicks on a link with TPA tracking
    // and the cookies are not set yet

    useSyncCookiesFromUrl<TpaBrandingCookieValues>({
        isParamsInUrl: isTpaParamsInUrl,
        currentCookieValues: tpaCookieValues,
        getCookieValuesFromStorage: getTpaBrandingValuesFromCookies,
        paramsFromUrl: tpaParamsInUrl as TpaBrandingCookieValues,
        formatCookie: formatTpaBrandingCookie,
        setCookiesFromUrl: setTpaBrandingCookiesFromUrl,
        setCookieValuesState: setTpaCookieValues,
        setContextState,
        deepCompare: false
    })

    // Sets TPA tracking cookies from customer data
    // This is used to set the cookies when the user is registered and the cookies are not set yet
    // and there are no TPA tracking values in the URL

    useSyncCookiesFromCustomerData<TpaBrandingCookieValues, TpaBrandingFromCustomerProfile>({
        isParamsInUrl: isTpaParamsInUrl,
        isRegistered,
        customerDataValue: tpaValueFromCustomerData,
        setCookiesFromCustomerData: setTpaBrandingCookiesFromCustomerData,
        getCookieValuesFromStorage: getTpaBrandingValuesFromCookies,
        setCookieValuesState: setTpaCookieValues,
        setContextState
    })

    const memoizedBuildPayload = useCallback(
        (cookie: string, profileValue: TpaBrandingFromCustomerProfile) =>
            formatTpaBrandingDataForCustomerData({
                tpaBrandingCookie: cookie,
                tpaValueFromCustomerData: profileValue
            }),
        []
    )

    // Determines if we should update customer profile with TPA tracking cookie
    useSyncCookieToCustomerProfile<string, TpaBrandingFromCustomerProfile, TpaBrandingCookieValues>(
        {
            isRegistered,
            isCustomerDataLoading,
            shouldQueryCustomerData,
            customerId,
            cookieValues: tpaCookieValues,
            customerProfileValue: tpaValueFromCustomerData,
            formatCookie: formatTpaBrandingCookie,
            shouldUpdateCookie: determineShouldUpdateTpaBrandingCookie,
            buildCustomerPayload: memoizedBuildPayload,
            mutateCustomerData: mutateCustomerTpaBrandingData
        }
    )
}

export default useTpaBrandingCookies
